import ExcelJS from 'exceljs';
import moment from 'moment/moment';

import { fc } from 'i18n';
import { ITepCompany, ITepLease } from 'types/tep';
import { TEP_RELEVANCE } from 'constants/tep';

interface IColumn {
  header: string;
  key: string;
  width: number;
}

interface IReportSummary {
  companies?: ITepCompany[];
  entryPasses?: ITepLease[];
  parameters?: {
    companies?: number[];
    entryPasses?: string[];
    type?: string;
    timeRange?: {
      since: string;
      until: string;
    };
  };
  at?: Date;
}

const WORKSHEET_NAME = 'Historical report';

const COLUMNS = [
  { Header: 'ID', accessor: 'id', width: 70 },
  { Header: 'Device Name', accessor: 'device.name' },
  { Header: 'Device IMEI', accessor: 'device.imei', width: 200 },
  { Header: 'Vehicle Plate Number', accessor: 'plateNumber', width: 200 },
  { Header: 'company', accessor: 'company' },
  { Header: 'email', accessor: 'email' },
  { Header: 'Drafted At', accessor: 'draftedAt' },
  { Header: 'TEP Start Date', accessor: 'startOn' },
  { Header: 'TEP End Date', accessor: 'endOn' },
  { Header: 'Issued At', accessor: 'issuedAt' },
  { Header: 'Returned On', accessor: 'returnedOn' },
  { Header: 'TEP Duration', accessor: 'tepDuration' },
];

const KEYS: Record<string, string> = {
  id: 'id',
  'device.name': 'name',
  'device.imei': 'imei',
  company: 'company',
  email: 'email',
  plateNumber: 'plateNumber',
  startOn: 'startOn',
  endOn: 'endOn',
  draftedAt: 'draftedAt',
  issuedAt: 'issuedAt',
  returnedOn: 'returnedOn',
  tepDuration: 'tepDuration',
};

const KEYS_LIST = [
  'id',
  'device.name',
  'device.imei',
  'company',
  'email',
  'plateNumber',
  'startOn',
  'endOn',
  'draftedAt',
  'issuedAt',
  'returnedOn',
  'tepDuration',
];

const COLORS = {
  headerBackground: 'FF4472C4',
  headerText: 'FFFFFFFF',
  availableStatus: 'ff1c7903',
  defaultText: 'ff000000',
  expiredDate: 'ffc92828',
};

const getFormattedDate = (date?: string): string => (date ? moment(date).format('YYYY-MM-DD') : '-');

const getExportedColumns = () =>
  COLUMNS.map((column) => ({
    header: fc(column.Header),
    key: KEYS[column.accessor],
    width: 30,
  }));

const getFormattedData = (data: ITepLease[]): Partial<Record<string, string>>[] => {
  const formattedData: Partial<Record<string, string>>[] = [];

  for (const lease of data || []) {
    const formattedEntry: Partial<Record<string, string>> = {};

    for (const accessor of KEYS_LIST) {
      switch (accessor) {
        case 'id':
          formattedEntry.id = String(lease.id);
          break;
        case 'device.name':
          formattedEntry.name = lease.device.name;
          break;
        case 'device.imei':
          formattedEntry.imei = lease.device.uri;
          break;
        case 'company':
          formattedEntry.company = lease.company || '-';
          break;
        case 'email':
          formattedEntry.email = lease.email || '-';
          break;
        case 'plateNumber':
          formattedEntry.plateNumber = lease.plateNumber || '-';
          break;
        case 'startOn':
          formattedEntry.startOn = getFormattedDate(lease.startOn);
          break;
        case 'endOn':
          formattedEntry.endOn = getFormattedDate(lease.endOn);
          break;
        case 'draftedAt':
          formattedEntry.draftedAt = getFormattedDate(lease.draftedAt);
          break;
        case 'issuedAt':
          formattedEntry.issuedAt = getFormattedDate(lease.issuedAt);
          break;
        case 'returnedOn':
          formattedEntry.returnedOn = getFormattedDate(lease.returnedOn);
          break;
        case 'tepDuration':
          if (!lease.returnedOn) {
            formattedEntry.tepDuration = '-';
          } else {
            const startDate = moment(lease.startOn);
            const returnDate = moment(lease.returnedOn);
            const durationDays = returnDate.diff(startDate, 'days') + 1;
            formattedEntry.tepDuration = `${durationDays} day(s)`;
          }
          break;
      }
    }

    formattedData.push(formattedEntry);
  }

  return formattedData;
};

const createWorksheet = (
  workbook: ExcelJS.Workbook,
  formattedData: Partial<Record<string, string>>[],
  columns: IColumn[],
) => {
  const worksheet = workbook.addWorksheet(WORKSHEET_NAME);
  const isEndOn = !!columns.find((column) => column.key === 'endOn');
  const isStatus = !!columns.find((column) => column.key === 'status');

  worksheet.columns = columns;

  worksheet.getRow(1).eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBackground } };
    cell.font = { bold: true, color: { argb: COLORS.headerText } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  formattedData.forEach((entryPass) => {
    const row = worksheet.addRow(entryPass);

    if (isStatus) {
      const statusCell = row.getCell('status');

      statusCell.font = {
        color: {
          argb: entryPass.status === 'Available' ? COLORS.availableStatus : COLORS.defaultText,
        },
      };
    }

    if (isEndOn) {
      const endOnCell = row.getCell('endOn');
      const isExpired = entryPass.endOn && moment(entryPass.endOn, 'YYYY-MM-DD').isBefore(new Date(), 'day');

      endOnCell.font = {
        color: {
          argb: isExpired ? COLORS.expiredDate : COLORS.defaultText,
        },
      };
    }

    const tepDurationCell = row.getCell('tepDuration');
    if (
      tepDurationCell &&
      entryPass.returnedOn &&
      entryPass.endOn &&
      moment(entryPass.returnedOn, 'YYYY-MM-DD').isAfter(moment(entryPass.endOn, 'YYYY-MM-DD'))
    ) {
      tepDurationCell.font = {
        color: {
          argb: COLORS.expiredDate,
        },
      };
    }
  });

  return { worksheet, isEndOn, isStatus };
};

const downloadFile = (blob: Blob, filename: string): void => {
  try {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up
  } catch (error) {
    throw new Error('Failed to download file');
  }
};

const addReportSummaryToWorksheet = (worksheet: ExcelJS.Worksheet, reportSummary?: IReportSummary): number => {
  if (!reportSummary) {
    return 0;
  }

  const { companies, entryPasses, parameters, at } = reportSummary;
  const { companies: companiesIds, entryPasses: entryPassesIds, type, timeRange } = parameters || {};

  let rowIndex = 1;

  if (at) {
    const row = worksheet.addRow([fc('generated at'), moment(at).format('YYYY-MM-DD HH:mm:ss')]);
    rowIndex++;
  }

  if (companies && companiesIds) {
    const companiesText = companiesIds
      .map((id) => companies.find((company) => company.id === id)?.name)
      .filter(Boolean)
      .join(', ');
    if (companiesText) {
      const row = worksheet.addRow([fc('company'), companiesText]);
      rowIndex++;
    }
  }

  if (entryPasses && entryPassesIds) {
    const entryPassesText = entryPassesIds
      .map((uri) => entryPasses.find((entryPass) => entryPass.device.uri === uri)?.device.name)
      .filter(Boolean)
      .join(', ');
    if (entryPassesText) {
      const row = worksheet.addRow([fc('device'), entryPassesText]);
      rowIndex++;
    }
  }

  if (timeRange) {
    const timeRangeText = `${moment(timeRange.since).format('YYYY-MM-DD')}~${moment(timeRange.until).format(
      'YYYY-MM-DD',
    )}`;
    const row = worksheet.addRow([fc('period'), timeRangeText]);
    rowIndex++;
  }

  if (type) {
    const row = worksheet.addRow([fc('type'), TEP_RELEVANCE[type]]);
    rowIndex++;
  }

  worksheet.addRow([]);
  rowIndex++;

  return rowIndex;
};

const exportXlsx = async (data: ITepLease[], reportSummary?: IReportSummary): Promise<void> => {
  try {
    const formattedData = getFormattedData(data);
    const columns = getExportedColumns();
    const workbook = new ExcelJS.Workbook();

    const worksheet = workbook.addWorksheet(WORKSHEET_NAME);

    const summaryRows = addReportSummaryToWorksheet(worksheet, reportSummary);

    // Add column headers
    const headerRow = worksheet.addRow(columns.map((col) => col.header));
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBackground } };
      cell.font = { bold: true, color: { argb: COLORS.headerText } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    // Add data rows
    formattedData.forEach((entryPass) => {
      const row = worksheet.addRow(Object.values(entryPass));

      // Apply formatting for endOn column if it exists
      const endOnIndex = columns.findIndex((col) => col.key === 'endOn');
      if (endOnIndex !== -1) {
        const endOnCell = row.getCell(endOnIndex + 1);
        const isExpired = entryPass.endOn && moment(entryPass.endOn, 'YYYY-MM-DD').isBefore(new Date(), 'day');

        endOnCell.font = {
          color: {
            argb: isExpired ? COLORS.expiredDate : COLORS.defaultText,
          },
        };
      }

      // Apply formatting for tepDuration column if returnedOn is after endOn
      const tepDurationIndex = columns.findIndex((col) => col.key === 'tepDuration');
      if (
        tepDurationIndex !== -1 &&
        entryPass.returnedOn &&
        entryPass.endOn &&
        moment(entryPass.returnedOn, 'YYYY-MM-DD').isAfter(moment(entryPass.endOn, 'YYYY-MM-DD'))
      ) {
        const tepDurationCell = row.getCell(tepDurationIndex + 1);
        tepDurationCell.font = {
          color: {
            argb: COLORS.expiredDate,
          },
        };
      }
    });

    // Set column widths
    columns.forEach((column, index) => {
      worksheet.getColumn(index + 1).width = column.width || 30;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    downloadFile(blob, `${WORKSHEET_NAME}.xlsx`);
  } catch (error) {
    throw new Error('Failed to export to XLSX');
  }
};

const exportCsv = (data: ITepLease[], reportSummary?: IReportSummary) => {
  try {
    const formattedData = getFormattedData(data);
    const columns = getExportedColumns();
    const workbook = new ExcelJS.Workbook();

    const { worksheet } = createWorksheet(workbook, formattedData, columns);

    let csvContent = '';

    // Add report summary information if available
    if (reportSummary) {
      const { companies, entryPasses, parameters, at } = reportSummary;
      const { companies: companiesIds, entryPasses: entryPassesIds, type, timeRange } = parameters || {};

      // Add generated at information
      if (at) {
        csvContent += `"${fc('generated at')}","${moment(at).format('YYYY-MM-DD HH:mm:ss')}"\n`;
      }

      // Add company information
      if (companies && companiesIds) {
        const companiesText = companiesIds
          .map((id) => companies.find((company) => company.id === id)?.name)
          .filter(Boolean)
          .join(', ');
        if (companiesText) {
          csvContent += `"${fc('company')}","${companiesText}"\n`;
        }
      }

      // Add entry passes information
      if (entryPasses && entryPassesIds) {
        const entryPassesText = entryPassesIds
          .map((id) => entryPasses.find((entryPass) => entryPass.id === Number(id))?.device.name)
          .filter(Boolean)
          .join(', ');
        if (entryPassesText) {
          csvContent += `"${fc('device')}","${entryPassesText}"\n`;
        }
      }

      // Add time range information
      if (timeRange) {
        const timeRangeText = `${moment(timeRange.since).format('YYYY-MM-DD')}~${moment(timeRange.until).format(
          'YYYY-MM-DD',
        )}`;
        csvContent += `"${fc('period')}","${timeRangeText}"\n`;
      }

      // Add type information
      if (type) {
        csvContent += `"${fc('type')}","${TEP_RELEVANCE[type]}"\n`;
      }

      // Add a blank line to separate summary from table
      csvContent += '\n';
    }

    worksheet.getRow(1).eachCell((cell) => {
      csvContent += `"${cell.text.replace(/"/g, '""')}"` + ',';
    });
    csvContent = csvContent.slice(0, -1) + '\n';

    for (let i = 2; i <= worksheet.rowCount; i++) {
      worksheet.getRow(i).eachCell((cell) => {
        csvContent += `"${cell.text.replace(/"/g, '""')}"` + ',';
      });
      csvContent = csvContent.slice(0, -1) + '\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    downloadFile(blob, `${WORKSHEET_NAME}.csv`);
  } catch (error) {
    throw new Error('Failed to export to CSV');
  }
};

export const exportTepHistoryReport = async (
  format: string,
  data: ITepLease[],
  reportSummary?: IReportSummary,
): Promise<void> => {
  switch (format.toLowerCase()) {
    case 'xlsx':
      return await exportXlsx(data, reportSummary);
    case 'csv':
      return exportCsv(data, reportSummary);
  }
};
